// Pages holding unsaved edits, so in-app navigation can ask before it drops them.
import { useEffect, useRef } from 'react'

interface Holder {
  dirty: () => boolean
  /** The address (#…) the edits live under; a move that stays under it keeps them. */
  scope: () => string | undefined
}

const holders = new Set<Holder>()
let passing = false

/** Whether `to` is the address `scope` or one below it (#a/b is under #a, #ab is not). */
export function within(to: string, scope: string): boolean {
  return to === scope || to.startsWith(scope + '/')
}

/** Whether a move would drop unsaved edits; with `to`, edits that stay on screen there are not counted. */
export function hasUnsaved(to?: string): boolean {
  if (passing) return false
  for (const h of holders) {
    if (!h.dirty()) continue
    const scope = h.scope()
    if (to !== undefined && scope && within(to, scope)) continue
    return true
  }
  return false
}

/** The person chose Discard: the next navigation goes through unasked. */
export function passUnsaved(): void {
  passing = true
}

/** Called by the navigation the pass was for, once it has happened. */
export function endPass(): void {
  passing = false
}

/** Registers a page's unsaved state for as long as it is mounted, under `scope` when it lives at an address. */
export function useUnsaved(dirty: boolean, scope?: string): void {
  const ref = useRef(dirty)
  ref.current = dirty
  const scopeRef = useRef(scope)
  scopeRef.current = scope
  useEffect(() => {
    const holder: Holder = { dirty: () => ref.current, scope: () => scopeRef.current }
    holders.add(holder)
    return () => {
      holders.delete(holder)
    }
  }, [])
  useEffect(() => {
    if (dirty) passing = false
  }, [dirty])
}
